/**
 * Shared browser-audio / input hygiene helpers.
 *
 * Added in the Claude cleanup pass so every instrument handles the same
 * platform quirks the same way. No dependencies; safe to import anywhere
 * that runs in the browser (all functions no-op during SSR).
 */

/* ------------------------------------------------------------------ */
/* Audio unlock / resume                                               */
/* ------------------------------------------------------------------ */

type NavigatorWithAudioSession = Navigator & {
  audioSession?: { type?: string };
};

/**
 * iOS 17+/Safari: ask for "playback" so the hardware silent switch does not
 * mute Web Audio. Harmless elsewhere.
 */
export function primeAudioSession(): void {
  if (typeof navigator === "undefined") return;
  try {
    const session = (navigator as NavigatorWithAudioSession).audioSession;
    if (session && session.type !== "playback") session.type = "playback";
  } catch {
    /* ignore */
  }
}

/**
 * Resume a context that is "suspended" or Safari's "interrupted" state.
 * Never throws; returns the resume promise (or a resolved one).
 */
export function resumeAudio(ctx: BaseAudioContext | null | undefined): Promise<void> {
  if (!ctx || !(ctx instanceof AudioContext)) return Promise.resolve();
  primeAudioSession();
  const state = ctx.state as string;
  if (state === "suspended" || state === "interrupted") {
    return ctx.resume().catch(() => undefined);
  }
  return Promise.resolve();
}

/**
 * Keep an AudioContext alive across the events browsers actually treat as a
 * user activation. On touch screens `pointerdown` does NOT count (only
 * `pointerup`/`touchend`/`click`), so unlocking only on pointerdown leaves the
 * first tap silent on phones. Also resumes when the tab becomes visible again
 * and after Safari's "interrupted" state (calls, Siri, other apps).
 *
 * `getCtx` is called lazily so it can return null before the engine exists.
 * Returns a cleanup function.
 */
export function installAudioUnlock(
  getCtx: () => BaseAudioContext | null | undefined,
): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => {
    void resumeAudio(getCtx());
  };
  const events = ["pointerup", "touchend", "click", "keydown"] as const;
  for (const e of events) window.addEventListener(e, handler, { capture: true, passive: true });
  const onVisible = () => {
    if (document.visibilityState === "visible") handler();
  };
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("pageshow", handler);
  return () => {
    for (const e of events) window.removeEventListener(e, handler, { capture: true });
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("pageshow", handler);
  };
}

/* ------------------------------------------------------------------ */
/* Storage                                                             */
/* ------------------------------------------------------------------ */

/** localStorage.getItem + JSON.parse that never throws. */
export function readStored<T = unknown>(key: string): T | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(key);
    return raw == null ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

/** JSON.stringify + localStorage.setItem that never throws (private mode, quota, sandboxed iframes). */
export function writeStored(key: string, value: unknown): boolean {
  try {
    if (typeof localStorage === "undefined") return false;
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

/**
 * Debounced writer for state that changes on every pointer move. Call
 * `write(value)` as often as you like; it persists at most every `ms`, and
 * `flush()` (also run on pagehide) writes the latest value immediately.
 */
export function createDebouncedStore(key: string, ms = 250) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: { value: unknown } | null = null;
  const flush = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (pending) writeStored(key, pending.value);
    pending = null;
  };
  if (typeof window !== "undefined") window.addEventListener("pagehide", flush);
  return {
    write(value: unknown) {
      pending = { value };
      if (timer) clearTimeout(timer);
      timer = setTimeout(flush, ms);
    },
    flush,
  };
}

/* ------------------------------------------------------------------ */
/* Keyboard                                                            */
/* ------------------------------------------------------------------ */

const TEXT_INPUT_TYPES = new Set([
  "text", "search", "email", "url", "tel", "password", "number", "date", "time",
  "datetime-local", "month", "week",
]);

/** True while the user is typing into a text field (keys must not play notes). */
export function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement) return TEXT_INPUT_TYPES.has(target.type || "text");
  return false;
}

/**
 * True when the focused element is a control that uses Space/Enter itself
 * (buttons, links, checkboxes, sliders, tabs...). Global Space/Enter
 * shortcuts should step aside so keyboard users can still press buttons.
 * Range sliders are included: Space/arrow keys belong to them.
 */
export function isActivatableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (isTextEntryTarget(target)) return true;
  if (target instanceof HTMLButtonElement) return true;
  if (target instanceof HTMLAnchorElement && target.href) return true;
  if (target instanceof HTMLInputElement) return true;
  const role = target.getAttribute("role");
  return !!role && /^(button|link|checkbox|switch|radio|tab|menuitem|option|slider|spinbutton|combobox)$/.test(role);
}

/** True when an arrow key should go to the focused control instead of a global shortcut. */
export function isArrowKeyTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (isTextEntryTarget(target)) return true;
  if (target instanceof HTMLInputElement && target.type === "range") return true;
  const role = target.getAttribute("role");
  return !!role && /^(slider|spinbutton|radio|tab|listbox|option|menuitem)$/.test(role);
}

/**
 * Release everything when the page loses focus, so a key or finger that was
 * down while the user switched tabs/apps can't leave a note droning.
 * Returns a cleanup function.
 */
export function onFocusLost(release: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onHide = () => {
    if (document.visibilityState === "hidden") release();
  };
  window.addEventListener("blur", release);
  document.addEventListener("visibilitychange", onHide);
  return () => {
    window.removeEventListener("blur", release);
    document.removeEventListener("visibilitychange", onHide);
  };
}
